import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import type { AuthUser } from '@tupai/firebase-auth';
import { firebaseAuth } from '@/lib/firebase';
import { publicWebOrigin } from '@/lib/web-origin';
import { AppShell } from '@tupai/shell';
import hello from '@tupai/tokens/assets/spot/hello.svg';
import { useAuthStore } from '@/store/auth-store';
import { useTranslation } from '@tupai/i18n/react';
import { firebaseAuthErrorKey } from '@/lib/firebase-auth-errors';

export const Route = createFileRoute('/verify-email')({
  validateSearch: z.object({ oobCode: z.string().optional(), changed: z.string().optional() }),
  component: VerifyEmailPage,
});

// Same key the details sheet writes: the email the parent is changing from.
async function pendingEmailKey() {
  const firebaseEmail = (await firebaseAuth.currentUser())?.email;
  const current = useAuthStore.getState().email || firebaseEmail || '';
  return `tupai_parent_pending_email_${current}`;
}

// The change is done: show the new address in the details sheet straight
// away, since Firebase ends its session and can no longer supply it.
async function finishEmailChange() {
  const key = await pendingEmailKey();
  const newEmail = localStorage.getItem(key);
  if (newEmail) useAuthStore.getState().setEmail(newEmail);
  localStorage.removeItem(key);
}

function VerifyEmailPage() {
  const { t } = useTranslation('auth', { keyPrefix: 'verifyEmailPage' });
  const { t: tAuth } = useTranslation('auth');
  const { oobCode, changed } = Route.useSearch();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(null);
  useEffect(() => {
    firebaseAuth.currentUser().then(setUser, () => {});
  }, []);

  useEffect(() => {
    if (!oobCode) {
      // Firebase's hosted page already verified the email and sent us here
      // with only our continue URL — nothing left to apply.
      if (changed) {
        void finishEmailChange();
        setStatus('success');
        return;
      }
      setStatus('error');
      setErrorMessage(t('invalidLink'));
      return;
    }

    firebaseAuth
      .applyActionCode(oobCode)
      .then(async () => {
        await finishEmailChange();
        setStatus('success');
      })
      .catch((err: any) => {
        setStatus('error');
        if (err.code === 'auth/expired-action-code') {
          setErrorMessage(t('expired'));
        } else if (err.code === 'auth/invalid-action-code') {
          setErrorMessage(t('used'));
        } else {
          const key = firebaseAuthErrorKey(err);
          setErrorMessage(key ? tAuth(key) : t('error'));
        }
      });
  }, [oobCode, changed]);

  const handleRequestNew = async () => {
    if (!user) {
      navigate({ to: '/login' });
      return;
    }
    const email = localStorage.getItem(await pendingEmailKey());
    if (!email) {
      navigate({ to: '/profile' });
      return;
    }
    try {
      // `changed=1` marks a redirect from Firebase's hosted action page,
      // which has already applied the code and drops `oobCode`.
      await firebaseAuth.verifyBeforeUpdateEmail(
        email,
        publicWebOrigin() + '/verify-email?changed=1'
      );
      alert(t('newSent'));
      navigate({ to: '/profile' });
    } catch (err) {
      const key = firebaseAuthErrorKey(err);
      alert(key ? tAuth(key) : t('sendFailed'));
    }
  };

  return (
    <AppShell
      variant="fullscreen"
      tone="surface"
      className="relative items-center justify-center overflow-y-auto px-8 py-8"
    >
      <div className="flex w-full max-w-sm flex-col">
        <div className="mb-8 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-pill bg-bg text-primary">
            <img src={hello} alt="" className="w-12 h-12" style={{ objectFit: 'contain' }} />
          </div>
        </div>

        {status === 'verifying' && (
          <h1 className="mb-8 font-display text-2xl text-content text-center">{t('checking')}</h1>
        )}

        {status === 'success' && (
          <div className="text-center">
            <h1 className="mb-4 font-display text-2xl text-content">{t('verified')}</h1>
            <p className="mb-8 text-sm text-content">{t('verifiedBody')}</p>
            <button
              onClick={() => navigate({ to: user ? '/profile' : '/login' })}
              className="flex min-h-[44px] w-full items-center justify-center rounded-md bg-primary font-bold text-surface hover:brightness-95"
            >
              {t('continue')}
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <h1 className="mb-4 font-display text-2xl text-content">{t('failed')}</h1>
            <p className="mb-6 text-sm text-error">{errorMessage}</p>

            {user && (
              <button
                onClick={handleRequestNew}
                className="flex min-h-[44px] w-full items-center justify-center rounded-md border border-border font-bold text-content hover:bg-bg mb-4"
              >
                {t('requestNewLink')}
              </button>
            )}

            <button
              onClick={() => navigate({ to: user ? '/profile' : '/login' })}
              className="flex min-h-[44px] w-full items-center justify-center rounded-md bg-primary font-bold text-surface hover:brightness-95"
            >
              {user ? t('backToProfile') : t('backToLogin')}
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}
